import CharacterClass, {
  characterClasses,
} from 'app/frontend/types/custom/CharacterClass'
import { RecommendedSetup } from 'app/frontend/base/api/event'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'

export type EventActivityRoleSetup = {
  tank: number
  heal: number
  melee: number
  ranged: number
}

export type EventActivityIssues = {
  missingClasses: CharacterClass[]
  tooManyPlayers: boolean
  selectedCount: number
  raidSize: number | null
  issueCount: number
  recommendationCount: number
  hasRecommendedSetupMismatch: boolean
  recommendedSetup: RecommendedSetup | null
  recommendedRoleSetup: EventActivityRoleSetup | null
  selectedRoleSetup: EventActivityRoleSetup
}

type ResolveEventActivityIssuesInput = {
  activity: wowaudit.EventActivity
  entries: EventActivityParticipantEntry[]
  unavailableStatuses: wowaudit.EventParticipantReference['status'][]
  recommendedSetup?: RecommendedSetup | null
}

const minimumRecommendedSetupFraction = 0.1
const resolveRoleSetupFraction = (
  distribution: RecommendedSetup['tanks']['distribution'],
  amount: number,
) => distribution.find((entry) => entry.amount === amount)?.fraction ?? 0

export const resolveEventActivityIssues = ({
  activity,
  entries,
  unavailableStatuses,
  recommendedSetup,
}: ResolveEventActivityIssuesInput): EventActivityIssues => {
  const selectedEntries = entries.filter((entry) => {
    if (unavailableStatuses.includes(entry.participant.status)) return false
    return entry.selections[activity.id]?.selected
  })
  const availableEntries = entries.filter(
    (entry) => !unavailableStatuses.includes(entry.participant.status),
  )
  const selectedCount = selectedEntries.length
  const availableRepresentedClasses = new Set<CharacterClass>(
    availableEntries
      .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
      .map((character) => character.characterClass as CharacterClass),
  )
  const selectedClasses = new Set<CharacterClass>(
    selectedEntries
      .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
      .map((character) => character.characterClass as CharacterClass),
  )
  const missingClasses = characterClasses.filter(
    (characterClass) => !selectedClasses.has(characterClass),
  )
  const actionableMissingClasses = missingClasses.filter((characterClass) =>
    availableRepresentedClasses.has(characterClass),
  )
  const recommendedMissingClasses = missingClasses.filter(
    (characterClass) => !availableRepresentedClasses.has(characterClass),
  )
  const raidSize = activity.raidSize ?? null
  const tooManyPlayers = raidSize !== null ? selectedCount > raidSize : false
  const selectedRoleSetup = selectedEntries.reduce<EventActivityRoleSetup>(
    (acc, entry) => {
      const role = entry.selections[activity.id]?.role
      if (role === 'tank') acc.tank += 1
      if (role === 'heal') acc.heal += 1
      if (role === 'melee') acc.melee += 1
      if (role === 'ranged') acc.ranged += 1
      return acc
    },
    {
      tank: 0,
      heal: 0,
      melee: 0,
      ranged: 0,
    },
  )
  const recommendedRoleSetup = recommendedSetup
    ? {
        tank: recommendedSetup.tanks.recommendation,
        heal: recommendedSetup.healers.recommendation,
        melee: recommendedSetup.melee.recommendation,
        ranged: recommendedSetup.ranged.recommendation,
      }
    : null
  const hasRecommendedSetupMismatch = recommendedSetup
    ? resolveRoleSetupFraction(
        recommendedSetup.tanks.distribution,
        selectedRoleSetup.tank,
      ) < minimumRecommendedSetupFraction ||
      resolveRoleSetupFraction(
        recommendedSetup.healers.distribution,
        selectedRoleSetup.heal,
      ) < minimumRecommendedSetupFraction ||
      resolveRoleSetupFraction(
        recommendedSetup.melee.distribution,
        selectedRoleSetup.melee,
      ) < minimumRecommendedSetupFraction ||
      resolveRoleSetupFraction(
        recommendedSetup.ranged.distribution,
        selectedRoleSetup.ranged,
      ) < minimumRecommendedSetupFraction
    : false

  return {
    missingClasses,
    tooManyPlayers,
    selectedCount,
    raidSize,
    hasRecommendedSetupMismatch,
    recommendedSetup: recommendedSetup ?? null,
    recommendedRoleSetup,
    selectedRoleSetup,
    issueCount:
      (actionableMissingClasses.length > 0 ? 1 : 0) + (tooManyPlayers ? 1 : 0),
    recommendationCount:
      (recommendedMissingClasses.length > 0 ? 1 : 0) +
      (hasRecommendedSetupMismatch ? 1 : 0),
  }
}
